import { CheckIcon, WarningCircleIcon } from '@phosphor-icons/react'
import { type FormEvent, useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  type AccountEmail,
  type EmailKeyChallenge,
  confirmAccountEmail,
  fetchAccountEmail,
  requestEmailConfirmCode,
} from '../../api/userland'
import { useAccountVault } from '../../hooks/useAccountVault'
import soft from '../../styles/soft.module.css'
import { emailCodeFailMessage, useResendCountdown, wellFormedCode } from '../auth/emailCode'
import { figs } from '../ui/fig'
import styles from './settings.module.css'

// Settings → Security → Email, for an email account: the address, and a code
// to confirm it. A password reset releases the wallet's login secret only to
// an address confirmed before the reset (operator, 2026-11-05), so accounts
// made before sign-up codes confirm here. On an account with a wallet the
// confirm also proves the account key: the API seals a nonce to it and the
// unlocked wallet opens it, so a session a reset handed out cannot confirm.

const UNLOCK_FIRST = 'Unlock the wallet first, then confirm the email.'

export function ConfirmEmail() {
  const vault = useAccountVault()
  const resend = useResendCountdown()
  const [challenge, setChallenge] = useState<EmailKeyChallenge | undefined>()
  const [account, setAccount] = useState<AccountEmail | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    fetchAccountEmail().then(
      (got) => live && setAccount(got),
      () => live && setLoadFailed(true)
    )
    return () => {
      live = false
    }
  }, [])

  const sendCode = async () => {
    setBusy(true)
    setError(null)
    try {
      const out = await requestEmailConfirmCode()
      if (out.verified) {
        setAccount((prev) => (prev ? { ...prev, verified: true } : prev))
      } else {
        setChallenge(out.keyChallenge)
        setCodeSent(true)
        resend.start()
      }
    } catch (err) {
      setError(emailCodeFailMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!wellFormedCode(code)) {
      setError('Enter the 6-digit code from the email.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      let keyProof: { token: string; proof: string } | undefined
      if (challenge) {
        // The same envelope as a wallet seed sealed to the account key.
        const proof = await vault
          .decryptSeed({
            version: 'client-vault-v1',
            cipherName: 'AES-256-GCM',
            nonceB64: challenge.nonceB64,
            ciphertextB64: challenge.ciphertextB64,
          })
          .catch(() => null)
        if (proof === null) {
          setError(UNLOCK_FIRST)
          return
        }
        keyProof = { token: challenge.token, proof }
      }
      setAccount(await confirmAccountEmail(code.trim(), keyProof))
      setCode('')
      setCodeSent(false)
      setChallenge(undefined)
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'key_proof_required'
          ? UNLOCK_FIRST
          : emailCodeFailMessage(err)
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className={styles.group} aria-label="Email">
      <div className={styles.groupHead}>
        <h3 className={styles.caption}>Email</h3>
        <span className={styles.hint}>{account ? figs(account.email) : loadFailed ? '—' : ''}</span>
      </div>
      {account?.verified ? (
        <div className={styles.tNote}>
          <CheckIcon size={14} weight="bold" aria-hidden="true" />
          {figs('Confirmed')}
        </div>
      ) : account && !codeSent ? (
        <div className={styles.passwordForm}>
          <button
            type="button"
            className={`${soft.action} ${styles.tAdd}`}
            disabled={busy}
            onClick={() => void sendCode()}
          >
            {figs(busy ? 'Sending…' : 'Confirm email')}
          </button>
        </div>
      ) : account ? (
        <form className={styles.passwordForm} onSubmit={(event) => void submit(event)}>
          <label className={`${soft.field} ${styles.tField}`}>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit code"
              aria-label="Code"
              disabled={busy}
            />
          </label>
          <button type="submit" className={`${soft.action} ${styles.tAdd}`} disabled={busy}>
            {figs(busy ? 'Checking…' : 'Confirm')}
          </button>
          <button
            type="button"
            className={`${soft.action} ${styles.tAdd}`}
            disabled={busy || resend.left > 0}
            onClick={() => void sendCode()}
          >
            {figs(resend.left > 0 ? `New code in ${resend.left} s` : 'New code')}
          </button>
        </form>
      ) : null}
      {error ? (
        <div className={styles.tError} role="alert">
          <WarningCircleIcon size={14} weight="bold" aria-hidden="true" />
          {figs(error)}
        </div>
      ) : null}
    </section>
  )
}
